import config, {envIsDev, envIsServerDeployment} from '../../config';

export type DatadogMode = 'saas' | 'server';

let isSaasDatadogEnabledAtRuntime = true;

/** Blocks SaaS mode for this page load; does not revoke SDK consent itself. */
export function disableSaasDatadogAtRuntime() {
  isSaasDatadogEnabledAtRuntime = false;
}

// SaaS publishes readiness after authentication or OneTrust consent. Server
// deployments use their explicit RUM opt-in instead.
declare global {
  interface Window {
    ReadyToInitDatadog?: boolean;
  }
}

/**
 * Resolves the currently eligible mode, including SaaS consent and shutdown.
 * Use as the shared eligibility check for initialization and runtime guards.
 * Server mode is config-based and ignores SaaS shutdown. Reevaluate SaaS mode
 * after consent or admin-mode changes; this does not indicate SDK initialization.
 */
export function getDatadogMode(): DatadogMode | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  if (envIsServerDeployment) {
    return getServerDatadogMode();
  }

  return getSaasDatadogMode();
}

/** Server RUM uses deployment opt-in only, never SaaS consent or runtime shutdown. */
function getServerDatadogMode(): DatadogMode | undefined {
  // Dedicated Cloud and Self-Managed/On-Prem require explicit RUM opt-in and
  // bypass SaaS readiness and consent gates. Development never sends server RUM.
  return envIsServerDeployment && config.DATADOG_RUM_ENABLED && !envIsDev
    ? 'server'
    : undefined;
}

/** SaaS uses consent and analytics policy; the server RUM opt-in is irrelevant. */
function getSaasDatadogMode(): DatadogMode | undefined {
  // Private environments never fall through to SaaS policy, even without a
  // recognized server deployment classification.
  if (config.ENVIRONMENT_IS_PRIVATE) {
    return undefined;
  }

  // DATADOG_DEBUG is a development-only SaaS override. It bypasses consent
  // readiness, normal SaaS gates, and the runtime shutdown flag.
  if (config.DATADOG_DEBUG && envIsDev) {
    return 'saas';
  }

  // Outside the debug override, development must not send SaaS telemetry.
  // Keep the analytics guard even though the consent bootstrap withholds
  // readiness when analytics are disabled, so inconsistent globals cannot
  // bypass the configured policy.
  if (envIsDev || config.ANALYTICS_DISABLED || !window.ReadyToInitDatadog) {
    return undefined;
  }

  return isSaasDatadogEnabledAtRuntime ? 'saas' : undefined;
}
